// The IMAGE edit bottom-sheet body: a fit-to-screen touch canvas (tap-select, one-finger move,
// two-finger pinch scale+rotate via SelectionOverlay) with undo/redo and the layer toolbar. The
// sheet shell (MobileBottomSheet) supplies the header/close and the "Ask Taka" footer; the score
// and the AI chat input live on the composer home, not here. Returns null without a composition.

import { useLayoutEffect, useRef, useState } from 'react'
import { Redo2, Undo2 } from 'lucide-react'
import { CompositionCanvas } from '../CompositionCanvas'
import { SelectionOverlay } from './SelectionOverlay'
import { MobileLayerToolbar } from './MobileLayerToolbar'
import { useComposerEditor } from './CompositionEditorProvider'

const INSET = 24 // breathing room around the fit-to-screen canvas

export function MobileCanvasEditor() {
  const ctx = useComposerEditor()
  const areaRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState<{ w: number; h: number } | null>(null)
  const [scale, setScale] = useState(1)

  useLayoutEffect(() => {
    const el = areaRef.current
    if (!el) return
    const update = () => setBox({ w: el.clientWidth, h: el.clientHeight })
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  if (!ctx) return null
  const { editor } = ctx

  return (
    <div data-mobile-editor className="relative flex h-full flex-col overflow-hidden bg-bg-2">
      {/* Undo / redo. */}
      <div className="flex flex-shrink-0 items-center justify-end gap-1 px-2 py-1.5">
        <button
          type="button"
          onClick={editor.undo}
          disabled={!editor.canUndo}
          aria-label="Undo"
          className="flex h-9 w-9 items-center justify-center rounded-full text-paper-dim active:bg-bg-3 disabled:opacity-30"
        >
          <Undo2 size={18} />
        </button>
        <button
          type="button"
          onClick={editor.redo}
          disabled={!editor.canRedo}
          aria-label="Redo"
          className="flex h-9 w-9 items-center justify-center rounded-full text-paper-dim active:bg-bg-3 disabled:opacity-30"
        >
          <Redo2 size={18} />
        </button>
      </div>

      {/* Canvas — the post fits centered with the whole composition visible. */}
      <div
        ref={areaRef}
        className="relative flex flex-1 items-center justify-center overflow-hidden"
      >
        <div
          className="grid-bg pointer-events-none absolute inset-0"
          data-grid-bg="emphasized"
          aria-hidden
        />
        {box && box.w > 0 && box.h > 0 && (
          <div ref={stageRef} data-canvas-visual className="relative w-fit">
            <CompositionCanvas
              composition={editor.comp}
              displayWidth={Math.max(1, box.w - INSET)}
              maxHeight={Math.max(1, box.h - INSET)}
              onScaleChange={i => setScale(i.scale)}
              suppressLayerId={editor.editingId ?? undefined}
            >
              <SelectionOverlay editor={editor} scale={scale} containerRef={stageRef} touch />
            </CompositionCanvas>
          </div>
        )}
      </div>

      {/* Manual layout tools. */}
      <MobileLayerToolbar />
    </div>
  )
}
